import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, KeyRound, LogOut, Shield, Trash2, UserCircle2 } from "lucide-react";
import { SwalWithTheme } from "../utils/sweetalert";
import { getUsuarioLogado, logout } from "../services/LoginService";
import {
  alterarMinhaSenha,
  atualizarMeuPerfil,
  desativarMeuDispositivoPush,
  inativarMinhaConta,
  listarMeusDispositivosPush,
  uploadMeuAvatar,
} from "../services/usuarioService";
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  FormField,
  Modal,
  PageLoader,
  StatusBadge,
  getAvatarInitials,
} from "../components/ui";
import WebPushDevicesSection from "../components/WebPushDevicesSection";
import { validarSenhaForte } from "../utils/validations";
import { useAppAlert } from "../context/alert-context";
import { validateAvatarFile } from "../utils/avatar";
import {
  ativarWebPush,
  desativarWebPush,
  enviarPushTeste,
  isPushSupported,
  obterDiagnosticoPushAtual,
  resetarAmbientePush,
  sincronizarInscricaoPushAtual,
} from "../services/notificationService";

function createDefaultPushState() {
  return {
    supported: isPushSupported(),
    permission: "unknown",
    subscribed: false,
    subscription: null,
    serviceWorkerRegistered: false,
    serviceWorkerActive: false,
    serviceWorkerScope: null,
    browserEndpoint: null,
    browserEndpointSuffix: null,
    browserError: null,
    bridgeOrigin: null,
    appBase: null,
    lastAction: null,
    lastStage: null,
    lastUpdatedAt: null,
    traceLines: [],
    keyLength: null,
    decodedKeyLength: null,
    decodedKeyFirstByte: null,
    existingSubscriptionEndpointSuffix: null,
    backendStatus: null,
    backendError: null,
    serverDiagnostics: null,
    serverDiagnosticsError: null,
  };
}

export default function MeuPerfil() {
  const navigate = useNavigate();
  const alert = useAppAlert();

  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pushDevices, setPushDevices] = useState([]);
  const [pushDevicesLoading, setPushDevicesLoading] = useState(true);
  const [pushState, setPushState] = useState(createDefaultPushState);
  const [pushActionLoading, setPushActionLoading] = useState(false);
  const [pushResetLoading, setPushResetLoading] = useState(false);
  const [pushTestLoading, setPushTestLoading] = useState(false);
  const [pushLastError, setPushLastError] = useState("");
  const [disableDeviceBusyId, setDisableDeviceBusyId] = useState(null);

  const [form, setForm] = useState({ nome: "", email: "", fotoPerfilUrl: "" });
  const [passwordForm, setPasswordForm] = useState({
    novaSenha: "",
    confirmarSenha: "",
  });

  const passwordProgress = useMemo(() => {
    const checks = [
      Boolean(String(passwordForm.novaSenha || "").trim()),
      Boolean(String(passwordForm.confirmarSenha || "").trim()),
      String(passwordForm.novaSenha || "") === String(passwordForm.confirmarSenha || "")
        && Boolean(String(passwordForm.novaSenha || "").trim()),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [passwordForm]);

  async function carregarPerfil() {
    try {
      const user = await getUsuarioLogado();
      setUsuario(user);
      setForm({
        nome: user.nome || "",
        email: user.email || "",
        fotoPerfilUrl: user.fotoPerfilUrl || "",
      });
    } catch {
      navigate("/");
    } finally {
      setLoading(false);
    }
  }

  async function carregarDispositivosPush(showLoader = false) {
    if (showLoader) {
      setPushDevicesLoading(true);
    }
    try {
      const devices = await listarMeusDispositivosPush();
      setPushDevices(devices);
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível carregar os dispositivos Web Push";
      alert.error("Web Push", message, { position: "top-right", duration: 2800 });
    } finally {
      if (showLoader) {
        setPushDevicesLoading(false);
      }
    }
  }

  async function syncPushState() {
    try {
      let state = await obterDiagnosticoPushAtual();
      if (state?.subscription) {
        try {
          await sincronizarInscricaoPushAtual();
          state = await obterDiagnosticoPushAtual();
        } catch (error) {
          state = {
            ...state,
            backendError: error?.response?.data?.message || error?.message || "Falha ao sincronizar a inscricao com o backend",
          };
        }
      }
      setPushState({ ...createDefaultPushState(), ...state });
    } catch {
      setPushState(createDefaultPushState());
    }
  }

  async function carregarPush(showLoader = false) {
    if (showLoader) {
      setPushDevicesLoading(true);
    }
    try {
      await Promise.all([carregarDispositivosPush(false), syncPushState()]);
    } finally {
      if (showLoader) {
        setPushDevicesLoading(false);
      }
    }
  }

  useEffect(() => {
    (async () => {
      await carregarPerfil();
      await carregarPush(true);
    })();
  }, []);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handlePasswordChange(event) {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handlePhotoChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const validationError = validateAvatarFile(file);
    if (validationError) {
      SwalWithTheme.error("Imagem invalida", validationError);
      return;
    }

    setSavingProfile(true);
    try {
      const updated = await uploadMeuAvatar(file);
      setForm((prev) => ({ ...prev, fotoPerfilUrl: updated?.fotoPerfilUrl || "" }));
      await carregarPerfil();
      setEditMode(false);
      SwalWithTheme.success("Sucesso", "Foto de perfil atualizada");
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível enviar a imagem selecionada";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function salvarPerfil() {
    if (!usuario) return;

    setSavingProfile(true);
    try {
      await atualizarMeuPerfil({
        nome: form.nome,
        fotoPerfilUrl: form.fotoPerfilUrl,
      });
      await carregarPerfil();
      setEditMode(false);
      SwalWithTheme.success("Sucesso", "Perfil atualizado");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar perfil";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function alterarSenha() {
    if (passwordForm.novaSenha !== passwordForm.confirmarSenha) {
      SwalWithTheme.error("Erro", "As senhas nao coincidem");
      return;
    }

    const validacao = validarSenhaForte(passwordForm.novaSenha);
    if (!validacao.valida) {
      SwalWithTheme.error("Senha invalida", validacao.mensagem);
      return;
    }

    try {
      await alterarMinhaSenha({
        novaSenha: passwordForm.novaSenha,
        confirmarSenha: passwordForm.confirmarSenha,
      });

      setShowPasswordModal(false);
      setPasswordForm({ novaSenha: "", confirmarSenha: "" });
      SwalWithTheme.success("Sucesso", "Senha alterada");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao alterar senha";
      SwalWithTheme.error("Erro", message);
    }
  }

  async function handleInativarConta() {
    const result = await SwalWithTheme.confirm(
      "Inativar conta",
      "Sua conta sera inativada e voce sera desconectado.",
      "Inativar",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await inativarMinhaConta();
      await logout();
      alert.success("Conta inativada", "Sua conta foi inativada e a sessao foi encerrada.", {
        position: "top-right",
        duration: 2600,
      });
      navigate("/");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao inativar conta";
      SwalWithTheme.error("Erro", message);
    }
  }

  async function handleEnablePush() {
    if (pushActionLoading) return;
    setPushActionLoading(true);
    try {
      await ativarWebPush();
      await carregarPush(false);
      setPushLastError("");
      alert.success("Web Push ativado", "Este navegador agora pode receber alertas.", {
        position: "top-right",
        duration: 3200,
      });
    } catch (error) {
      const message = error?.message || error?.response?.data?.message || "Não foi possível ativar o Web Push";
      setPushLastError(message);
      alert.error("Web Push", message, { position: "top-right", duration: 3200 });
      await syncPushState();
    } finally {
      setPushActionLoading(false);
    }
  }

  async function handleDisablePush() {
    if (pushActionLoading) return;
    setPushActionLoading(true);
    try {
      await desativarWebPush();
      await carregarPush(false);
      setPushLastError("");
      alert.info("Web Push desativado", "Os alertas foram desligados neste navegador.", {
        position: "top-right",
        duration: 3200,
      });
    } catch (error) {
      const message = error?.message || error?.response?.data?.message || "Não foi possível desativar o Web Push";
      setPushLastError(message);
      alert.error("Web Push", message, { position: "top-right", duration: 3200 });
      await syncPushState();
    } finally {
      setPushActionLoading(false);
    }
  }

  async function handleResetPush() {
    if (pushResetLoading || pushActionLoading) return;
    setPushResetLoading(true);
    try {
      await resetarAmbientePush();
      setPushLastError("");
      alert.info("Web Push resetado", "Recarregue a pagina para registrar o push novamente.", {
        position: "top-right",
        duration: 3200,
      });
      await carregarPush(false);
    } catch (error) {
      const message = error?.message || "Não foi possível resetar o ambiente do Web Push";
      setPushLastError(message);
      alert.error("Web Push", message, { position: "top-right", duration: 3200 });
    } finally {
      setPushResetLoading(false);
    }
  }

  async function handleTestPush() {
    if (pushTestLoading || pushActionLoading || pushResetLoading) return;
    setPushTestLoading(true);
    try {
      const result = await enviarPushTeste();
      await carregarPush(false);
      const delivered = Number(result?.deliveredCount || 0);
      const failed = Number(result?.failedCount || 0);
      const skipped = Number(result?.skippedCount || 0);
      const firstFailureDetail = Array.isArray(result?.deliveries)
        ? result.deliveries.find((item) => item?.status === "FAILED" || item?.status === "SKIPPED")?.detail
        : "";

      if (delivered > 0) {
        setPushLastError("");
        alert.success("Teste de Web Push", `Entregues: ${delivered}. Falhas: ${failed}. Ignorados: ${skipped}.`, {
          position: "top-right",
          duration: 4200,
        });
      } else {
        const message = firstFailureDetail || `Nenhum push foi entregue. Falhas: ${failed}. Ignorados: ${skipped}.`;
        setPushLastError(message);
        alert.warning("Teste de Web Push", message, { position: "top-right", duration: 5200 });
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Não foi possível executar o teste de Web Push";
      setPushLastError(message);
      alert.error("Teste de Web Push", message, { position: "top-right", duration: 4200 });
      await syncPushState();
    } finally {
      setPushTestLoading(false);
    }
  }

  async function handleDisablePushDevice(device) {
    const result = await SwalWithTheme.confirm(
      "Desativar dispositivo",
      "Esse navegador deixara de receber Web Push ate ser ativado novamente.",
      "Desativar dispositivo",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    setDisableDeviceBusyId(device.id);
    try {
      await desativarMeuDispositivoPush(device.id);
      await carregarPush(false);
      alert.success("Dispositivo desativado", "O Web Push foi desligado para este dispositivo salvo.", {
        position: "top-right",
        duration: 2800,
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível desativar o dispositivo";
      alert.error("Web Push", message, { position: "top-right", duration: 3200 });
    } finally {
      setDisableDeviceBusyId(null);
    }
  }

  if (loading) {
    return <PageLoader label="Carregando perfil..." />;
  }

  if (!usuario) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Nao foi possivel carregar seu perfil.</div>;
  }

  return (
    <div className="space-y-4">
      <header className="page-header">
        <h1 className="page-title">Meu perfil</h1>
        <p className="page-subtitle">Atualize sua foto, seus dados pessoais e as configuracoes de seguranca da conta.</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card className="xl:col-span-2" title="Informacoes pessoais">
          <div className="space-y-5">
            <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background/70 p-4 sm:flex-row sm:items-center">
              <Avatar className="h-28 w-28 border-border bg-muted text-2xl text-foreground">
                <AvatarImage src={form.fotoPerfilUrl || usuario.fotoPerfilUrl} alt={usuario.nome || "Usuario"} />
                <AvatarFallback>{getAvatarInitials(form.nome || usuario.nome)}</AvatarFallback>
                <AvatarBadge tone="online" size="lg" />
              </Avatar>

              <div className="min-w-0 flex-1 space-y-2">
                <div>
                  <p className="text-lg font-semibold text-foreground">{usuario.nome}</p>
                  <p className="text-sm text-muted-foreground">{usuario.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    status={usuario.cargo?.toLowerCase() === "admin" ? "Administrador" : "Comercial"}
                    variant={usuario.cargo?.toLowerCase() === "admin" ? "info" : "default"}
                  />
                  <StatusBadge status="Ativo" variant="success" />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium transition hover:bg-accent">
                  <Camera className="h-4 w-4" />
                  Foto
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </label>
                {(form.fotoPerfilUrl || usuario.fotoPerfilUrl) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, fotoPerfilUrl: "" }));
                      if (!editMode) setEditMode(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Nome" name="nome" value={form.nome} onChange={handleChange} disabled={!editMode || savingProfile} />
              <FormField label="Email" type="email" name="email" value={form.email} onChange={handleChange} disabled />
            </div>

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              {!editMode ? (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  Editar perfil
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditMode(false);
                      setForm({
                        nome: usuario.nome || "",
                        email: usuario.email || "",
                        fotoPerfilUrl: usuario.fotoPerfilUrl || "",
                      });
                    }}
                    disabled={savingProfile}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={salvarPerfil} disabled={savingProfile}>
                    {savingProfile ? "Salvando..." : "Salvar alteracoes"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="Seguranca">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => setShowPasswordModal(true)}>
                <KeyRound className="h-4 w-4" />
                Alterar senha
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={async () => {
                  await logout();
                  alert.success("Logout realizado com sucesso", "Sua sessao foi encerrada.", {
                    position: "top-right",
                    duration: 2200,
                  });
                  navigate("/");
                }}
              >
                <LogOut className="h-4 w-4" />
                Sair da conta
              </Button>
              <Button variant="danger" className="w-full justify-start" onClick={handleInativarConta}>
                <Shield className="h-4 w-4" />
                Inativar minha conta
              </Button>
            </div>
          </Card>

          <Card title="Resumo">
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono">{usuario.id}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Usuario</span>
                <span className="inline-flex items-center gap-2">
                  <UserCircle2 className="h-4 w-4" />
                  {usuario.nome}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Cargo</span>
                <span>{usuario.cargo?.toLowerCase() === "admin" ? "Administrador" : "Comercial"}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <WebPushDevicesSection
        title="Dispositivos e alertas"
        description="Gerencie os navegadores que podem continuar recebendo notificacoes mesmo sem login."
        devices={pushDevices}
        loading={pushDevicesLoading}
        onRefresh={() => carregarPush(true)}
        onDisableDevice={handleDisablePushDevice}
        disableDeviceBusyId={disableDeviceBusyId}
        currentEndpointSuffix={pushState?.browserEndpointSuffix}
        currentPushControls={{
          pushState,
          pushBusy: pushActionLoading,
          pushResetBusy: pushResetLoading,
          pushTestBusy: pushTestLoading,
          pushLastError,
          onEnablePush: handleEnablePush,
          onDisablePush: handleDisablePush,
          onResetPush: handleResetPush,
          onTestPush: handleTestPush,
        }}
      />

      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Alterar senha"
        onSave={alterarSenha}
        onCancel={() => {
          setShowPasswordModal(false);
          setPasswordForm({ novaSenha: "", confirmarSenha: "" });
        }}
        progress={passwordProgress}
        progressLabel="Preenchimento da senha"
      >
        <div className="space-y-3">
          <FormField
            label="Nova senha"
            type="password"
            name="novaSenha"
            value={passwordForm.novaSenha}
            onChange={handlePasswordChange}
            required
          />
          <FormField
            label="Confirmar nova senha"
            type="password"
            name="confirmarSenha"
            value={passwordForm.confirmarSenha}
            onChange={handlePasswordChange}
            required
          />
        </div>
      </Modal>
    </div>
  );
}
